// ---------------------------------------------------------------------------
// productUtils.js — pure utility functions shared across webapp components
// ---------------------------------------------------------------------------

export function isHexColor(value) {
  return /^#[0-9a-fA-F]{6}$/.test(String(value || "").trim());
}

export function normalizeHexColor(value, fallback) {
  const normalized = String(value || "").trim();
  return isHexColor(normalized) ? normalized.toLowerCase() : fallback;
}

export function hexToRgba(hexColor, alpha) {
  const hex = normalizeHexColor(hexColor, "#38bdf8").slice(1);
  const r = Number.parseInt(hex.slice(0, 2), 16);
  const g = Number.parseInt(hex.slice(2, 4), 16);
  const b = Number.parseInt(hex.slice(4, 6), 16);
  const a = Math.max(0, Math.min(1, Number(alpha) || 0));
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

export function roundCurrency(value) {
  const parsed = Number(value) || 0;
  return Math.round((parsed + Number.EPSILON) * 100) / 100;
}

export function formatCurrency(value) {
  const amount = Number(value) || 0;
  return `${amount.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

export function resolveVariantPrice(variant) {
  const effectiveDiscounted = Number(variant?.effectiveDiscounted);
  if (Number.isFinite(effectiveDiscounted) && effectiveDiscounted > 0) return roundCurrency(effectiveDiscounted);

  const effectiveBase = Number(variant?.effectiveBase);
  if (Number.isFinite(effectiveBase) && effectiveBase > 0) return roundCurrency(effectiveBase);

  const effective = Number(variant?.effectivePrice);
  if (Number.isFinite(effective) && effective > 0) return roundCurrency(effective);

  const raw = Number(variant?.price) || 0;
  const discount = Number(variant?.discount) || 0;
  if (discount <= 0) return roundCurrency(raw);
  return roundCurrency(raw - (raw * discount) / 100);
}

export function resolveProductStartingPrice(product) {
  if (!product) return 0;
  if (product.hasVariants && Array.isArray(product.variants) && product.variants.length) {
    const prices = product.variants
      .map((v) => resolveVariantPrice(v))
      .filter((p) => Number.isFinite(p) && p > 0);
    if (prices.length) return Math.min(...prices);
  }
  const effectiveBase = Number(product.price?.effectiveBase);
  const effectiveDiscounted = Number(product.price?.effectiveDiscounted);
  const discounted = Number(product.price?.discounted);

  if (Number.isFinite(effectiveBase) && effectiveBase > 0) {
    if (Number.isFinite(effectiveDiscounted) && effectiveDiscounted > 0 && effectiveDiscounted < effectiveBase) {
      return roundCurrency(effectiveDiscounted);
    }
    return roundCurrency(effectiveBase);
  }

  const base = Number(product.price?.base) || 0;
  if (Number.isFinite(discounted) && discounted > 0 && discounted < base) return roundCurrency(discounted);
  return roundCurrency(base);
}

export function resolveProductOriginalStartingPrice(product) {
  if (!product) return 0;

  if (product.hasVariants && Array.isArray(product.variants) && product.variants.length) {
    const rawPrices = product.variants
      .map((variant) => Number(variant?.effectiveBase ?? variant?.price))
      .filter((value) => Number.isFinite(value) && value > 0);
    if (rawPrices.length) return roundCurrency(Math.min(...rawPrices));
  }

  return roundCurrency(Number(product.price?.effectiveBase ?? product.price?.base) || 0);
}

export function resolveProductPriceInfo(product) {
  const current = resolveProductStartingPrice(product);
  const original = resolveProductOriginalStartingPrice(product);
  const hasDiscount = original > 0 && current > 0 && current < original;

  return {
    current,
    original,
    hasDiscount,
    currentLabel: product?.hasVariants ? `da ${formatCurrency(current)}` : formatCurrency(current),
    originalLabel: product?.hasVariants ? `da ${formatCurrency(original)}` : formatCurrency(original),
  };
}

export function resolveProductUnitLabel(product) {
  if (product?.pricingType?.label) return product.pricingType.label;
  return "";
}

/**
 * Returns { label: string, hasMultiple: boolean }
 * hasMultiple = true when variants have mixed pricingTypes
 */
export function resolveProductUnitInfo(product) {
  if (!product) return { label: "", hasMultiple: false };

  if (!product.hasVariants || !Array.isArray(product.variants) || !product.variants.length) {
    return { label: product?.pricingType?.label || "", hasMultiple: false };
  }

  const productLabel = product?.pricingType?.label || "";
  // Effective label per variant: own pricingType || product pricingType
  const variantLabels = product.variants.map((v) => v?.pricingType?.label || productLabel);
  const unique = new Set(variantLabels.filter(Boolean));

  if (unique.size > 1) return { label: "", hasMultiple: true };

  const label = [...unique][0] || productLabel;
  return { label, hasMultiple: false };
}

export function productPriceLabel(product) {
  const price = resolveProductStartingPrice(product);
  if (product?.hasVariants) return `da ${formatCurrency(price)}`;
  return formatCurrency(price);
}

/**
 * Resolves the max orderable quantity for a product (or variant).
 * - When inventoryManagement is false (default): only sold_out status is respected;
 *   maxQuantity is ignored and Infinity is returned for available products.
 * - When inventoryManagement is true: maxQuantity cap is also enforced.
 * - For variant-level availability pass the variant object as `product`.
 */
export function resolveMaxQuantity(product, inventoryManagement = false) {
  const status = String(product?.availability?.status || "available");
  if (status === "sold_out") return 0;
  if (!inventoryManagement) return Number.POSITIVE_INFINITY;
  const max = Number(product?.availability?.maxQuantity);
  if (Number.isFinite(max) && max > 0) return max;
  return Number.POSITIVE_INFINITY;
}

export function isConfigurableProduct(product) {
  return (
    Boolean(product?.hasVariants) ||
    (Array.isArray(product?.options) && product.options.length > 0) ||
    Boolean(product?.variablePricing?.enabled)
  );
}

export function getProductImage(product, fallback) {
  return product?.images?.[0]?.url || fallback;
}

// ---------------------------------------------------------------------------
// Option helpers
// ---------------------------------------------------------------------------

export function resolveOptionKey(option, index) {
  return String(option?._id || `option-${index}`);
}

export function resolveChoiceKey(choice, index) {
  return String(choice?._id || `choice-${index}`);
}

export function resolveChoiceAddon(choice) {
  const value = Number(choice?.priceAddon);
  return Number.isFinite(value) ? value : 0;
}

export function formatAddonLabel(value) {
  const parsed = Number(value) || 0;
  if (parsed === 0) return "Incluso";
  return `${parsed > 0 ? "+" : "-"} ${formatCurrency(Math.abs(parsed))}`;
}

export function normalizeProductOptions(options) {
  if (!Array.isArray(options)) return [];
  return options
    .map((option, index) => {
      const type = String(option?.type || "text");
      const label = String(option?.label || "").trim();
      if (!label) return null;
      const choices = Array.isArray(option?.choices)
        ? option.choices
            .map((choice, ci) => {
              const choiceLabel = String(choice?.label || "").trim();
              if (!choiceLabel) return null;
              return {
                _key: resolveChoiceKey(choice, ci),
                label: choiceLabel,
                description: String(choice?.description || "").trim(),
                priceAddon: resolveChoiceAddon(choice),
              };
            })
            .filter(Boolean)
        : [];
      return {
        _key: resolveOptionKey(option, index),
        type,
        label,
        description: String(option?.description || "").trim(),
        required: Boolean(option?.required),
        choices,
      };
    })
    .filter(Boolean);
}

export function normalizeExtraInfoBlocks(extraInfo) {
  if (!Array.isArray(extraInfo)) return [];
  return extraInfo
    .map((block, index) => {
      const type = String(block?.type || "text");
      if (type === "table") {
        const headers = Array.isArray(block?.headers)
          ? block.headers.map((h) => String(h || "").trim())
          : [];
        const rows = Array.isArray(block?.rows)
          ? block.rows
              .map((row) => (Array.isArray(row) ? row.map((cell) => String(cell || "").trim()) : []))
              .filter((row) => row.some((cell) => cell))
          : [];
        if (!headers.length && !rows.length) return null;
        if (!headers.length) return null;
        return {
          _key: String(block?._id || `extra-${index}`),
          type: "table",
          title: String(block?.title || "").trim(),
          headers,
          rows,
        };
      }
      if (type === "gallery") {
        const images = Array.isArray(block?.images)
          ? block.images
              .map((img, ii) => ({
                _key: String(img?._id || `img-${index}-${ii}`),
                url: String(img?.url || "").trim(),
              }))
              .filter((img) => img.url)
          : [];
        if (!images.length) return null;
        return {
          _key: String(block?._id || `extra-${index}`),
          type: "gallery",
          title: String(block?.title || "").trim(),
          images,
        };
      }
      if (type === "faq") {
        const items = Array.isArray(block?.items)
          ? block.items
              .map((item, ii) => {
                const question = String(item?.question || "").trim();
                const answer = String(item?.answer || "").trim();
                if (!question || !answer) return null;
                return { _key: String(item?._id || `faq-${index}-${ii}`), question, answer };
              })
              .filter(Boolean)
          : [];
        if (!items.length) return null;
        return {
          _key: String(block?._id || `extra-${index}`),
          type: "faq",
          title: String(block?.title || "").trim(),
          items,
        };
      }
      const content = String(block?.content || "").trim();
      if (!content) return null;
      return { _key: String(block?._id || `extra-${index}`), type: "text", content };
    })
    .filter(Boolean);
}

export function buildOptionValueMap(options) {
  if (!Array.isArray(options)) return {};
  return options.reduce((acc, option) => {
    acc[option._key] = option.type === "multiple_choice" ? [] : "";
    return acc;
  }, {});
}

export function isOptionValueMissing(option, value) {
  if (!option?.required) return false;
  if (option.type === "multiple_choice") return !Array.isArray(value) || value.length === 0;
  return String(value || "").trim() === "";
}

// ---------------------------------------------------------------------------
// Cart line helpers
// ---------------------------------------------------------------------------

export function serializeOptionValues(values) {
  const keys = Object.keys(values || {}).sort();
  return keys
    .map((key) => {
      const value = values[key];
      if (Array.isArray(value)) return `${key}:[${[...value].sort().join(",")}]`;
      return `${key}:${String(value || "").trim()}`;
    })
    .join("|");
}

export function cloneOptionValues(values) {
  const source = values || {};
  return Object.keys(source).reduce((acc, key) => {
    const value = source[key];
    acc[key] = Array.isArray(value) ? [...value] : value;
    return acc;
  }, {});
}

export function buildVariableSelectionToken(variableSelection) {
  const value = Number(variableSelection?.value);
  if (!Number.isFinite(value) || value <= 0) return "none";
  const unit = String(variableSelection?.unit || "").trim();
  return `${value}:${unit || "unit"}`;
}

export function resolveVariableSelectionMeta(product) {
  const vp = product?.variablePricing || {};
  return {
    enabled: Boolean(vp.enabled),
    minSelection: Number(vp.minSelection),
    stepSelection: Number(vp.stepSelection),
    maxSelection: Number(vp.maxSelection),
    selectionUnit: String(vp.selectionUnit || "").trim(),
    conversionFactor: Number(vp.conversionFactor) > 0 ? Number(vp.conversionFactor) : 1,
  };
}

export function resolveDefaultVariableSelection(product) {
  const meta = resolveVariableSelectionMeta(product);
  if (!meta.enabled) return null;

  const min = Number.isFinite(meta.minSelection) && meta.minSelection > 0 ? meta.minSelection : 1;
  const step = Number.isFinite(meta.stepSelection) && meta.stepSelection > 0 ? meta.stepSelection : 1;
  const max = Number.isFinite(meta.maxSelection) && meta.maxSelection > 0 ? meta.maxSelection : min;
  const initial = Math.min(Math.max(min, min), max);

  return {
    value: initial,
    unit: meta.selectionUnit,
    min,
    step,
    max,
    conversionFactor: meta.conversionFactor,
  };
}

export function buildCartLineId(productId, variantId, optionValues, variableSelection) {
  const signature = serializeOptionValues(optionValues || {});
  const variableToken = buildVariableSelectionToken(variableSelection);
  return `line:${productId}:${variantId || "none"}:${signature || "none"}:v-${variableToken}`;
}

export function calculateOptionAddonTotal(options, optionValues) {
  if (!Array.isArray(options) || !options.length) return 0;
  return roundCurrency(
    options.reduce((sum, option) => {
      const value = optionValues?.[option._key];
      if (option.type === "single_choice") {
        const selected = option.choices.find((c) => c._key === String(value || ""));
        return sum + (selected ? selected.priceAddon : 0);
      }
      if (option.type === "multiple_choice") {
        const selectedKeys = Array.isArray(value) ? value : [];
        return sum + option.choices
          .filter((c) => selectedKeys.includes(c._key))
          .reduce((s, c) => s + c.priceAddon, 0);
      }
      return sum;
    }, 0)
  );
}

export function buildOptionSummaries(options, optionValues) {
  if (!Array.isArray(options) || !options.length) return [];
  return options
    .map((option) => {
      const value = optionValues?.[option._key];
      if (option.type === "single_choice") {
        const selected = option.choices.find((c) => c._key === String(value || ""));
        if (!selected) return null;
        return { label: option.label, value: selected.label };
      }
      if (option.type === "multiple_choice") {
        const selectedKeys = Array.isArray(value) ? value : [];
        const selected = option.choices.filter((c) => selectedKeys.includes(c._key));
        if (!selected.length) return null;
        return { label: option.label, value: selected.map((c) => c.label).join(", ") };
      }
      const raw = String(value || "").trim();
      if (!raw) return null;
      return { label: option.label, value: raw };
    })
    .filter(Boolean);
}

export function resolveUnitPriceForSelection(product, variantId, optionValues, variableSelection) {
  if (!product) return 0;
  const options = normalizeProductOptions(product?.options);
  const selectedVariant =
    product?.hasVariants && Array.isArray(product?.variants)
      ? product.variants.find((v) => String(v._id) === String(variantId || ""))
      : null;
  let basePrice = selectedVariant ? resolveVariantPrice(selectedVariant) : resolveProductStartingPrice(product);

  if (product?.variablePricing?.enabled) {
    const selectionValue = Number(variableSelection?.value);
    const conversionFactor = Number(product?.variablePricing?.conversionFactor);
    if (Number.isFinite(selectionValue) && selectionValue > 0) {
      basePrice = basePrice * selectionValue * (Number.isFinite(conversionFactor) && conversionFactor > 0 ? conversionFactor : 1);
    }
  }

  return roundCurrency(basePrice + calculateOptionAddonTotal(options, optionValues));
}
